import React, { Suspense, useEffect, useState } from "react";
import { DocumentNode, useMutation, useQuery } from "@apollo/client";
import { loader } from "graphql.macro";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  IntlShape,
  useIntl,
} from "react-intl";
import Error from "../Error";
import { TextModal } from "../TextModal";
import { ReactComponent as Trash } from "../../assets/Trash.svg";
import { ReactComponent as DeleteSvg } from "../../assets/deleteMemberSvg.svg";
import dialogStyles from "../../assets/stylesheets/dialog.module.scss";
import styles from "./index.module.scss";

const GET_ROOM_REPORT_QUERY = loader(
  "../../schema/query/GetRoomReport.graphql"
);
const DELETE_TRANSCRIPT_MUTATION = loader(
  "../../schema/mutation/DeleteTranscript.graphql"
);
const DELETE_CHAT_LOG_MUTATION = loader(
  "../../schema/mutation/DeleteChatLog.graphql"
);
const SUMMARIZE_TRANSCRIPT_MUTATION = loader(
  "../../schema/mutation/SummarizeTranscript.graphql"
);

// with react-markdown, only when the minutes are shown
const MinutesModal = React.lazy(() => import("./MinutesModal"));

interface Data {
  room: {
    id: string;
    authenticatedUserIsModerator: boolean;
    transcripts: Transcript[];
    chatLog: ChatLog | null;
  };
}

interface Variables {
  slug: string;
}

// "fr" -> "français", the code itself when the browser doesn't know it
const languageName = (intl: IntlShape, code: string) =>
  intl.formatDisplayName(code, { type: "language" }) ?? code.toUpperCase();

// "2026-09-01T14:32:05Z" -> "2026-09-01_14-32"
const filenameDate = (insertedAt: string) => {
  const date = new Date(insertedAt);
  const pad = (value: number) => value.toString().padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `_${pad(date.getHours())}-${pad(date.getMinutes())}`
  );
};

// Only the user who created the meeting can delete what was saved of it
const DeleteButton = ({
  mutation,
  id,
  confirmation,
}: {
  mutation: DocumentNode;
  id: string;
  confirmation: React.ReactNode;
}) => {
  const intl = useIntl();
  const [show, setShow] = useState(false);
  const [remove, { loading, error }] = useMutation(mutation, {
    variables: { id },
    refetchQueries: ["GetRoomReport"],
    onCompleted: () => setShow(false),
  });

  return (
    <>
      <Tooltip
        title={intl.formatMessage({
          id: "actions.delete",
          defaultMessage: "Delete",
        })}
      >
        <IconButton type="button" onClick={() => setShow(true)}>
          <Trash />
        </IconButton>
      </Tooltip>

      <Modal open={show} onClose={() => setShow(false)}>
        <Box
          className={dialogStyles.dialog}
          sx={{ bgcolor: "background.paper" }}
        >
          <DeleteSvg />
          <Typography className={dialogStyles.title}>{confirmation}</Typography>
          <Error error={error} />
          <Box className={dialogStyles.actions}>
            <Button
              onClick={() => setShow(false)}
              className={dialogStyles.cancelButton}
              variant="contained"
              color="secondary"
            >
              <FormattedMessage id="actions.back" defaultMessage="Back" />
            </Button>
            <Button
              className={dialogStyles.validateButton}
              onClick={() => remove().catch(() => {})}
              disabled={loading}
              variant="contained"
            >
              <FormattedMessage id="action-delete.yes" defaultMessage="Yes" />
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

// Shows the content in a modal, where it can also be downloaded
const ShowButton = ({
  title,
  filename,
  content,
}: {
  title: React.ReactNode;
  filename: string;
  content: string;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        size="small"
        onClick={() => setOpen(true)}
      >
        <FormattedMessage id="actions.show" defaultMessage="Show" />
      </Button>
      <TextModal
        open={open}
        title={title}
        content={content}
        filename={filename}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

// The minutes of the meeting, written by an LLM from a transcription when the
// owner of the meeting asks for them (see Meetingrooms.Meeting.Summaries in the
// backend). It takes up to a few minutes, the page asks again meanwhile.
const Minutes = ({
  transcript,
  isOwner,
  slug,
}: {
  transcript: Transcript;
  isOwner: boolean;
  slug: string;
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [summarize, { loading, error }] = useMutation(
    SUMMARIZE_TRANSCRIPT_MUTATION,
    { variables: { id: transcript.id } }
  );
  const status = transcript.summaryStatus;
  // the transcription grew since the minutes were written (the meeting went
  // on), "Write again" takes the end into account
  const outdated =
    status === "done" &&
    !!transcript.summarizedAt &&
    !!transcript.updatedAt &&
    new Date(transcript.updatedAt) > new Date(transcript.summarizedAt);
  const title = intl.formatMessage({
    id: "meetingCard.minutes",
    defaultMessage: "Minutes",
  });

  const write = (
    <Button
      variant={status === "done" ? "text" : "outlined"}
      color="secondary"
      size="small"
      disabled={loading}
      onClick={() => summarize().catch(() => {})}
    >
      {status === "done" ? (
        <FormattedMessage
          id="meetingCard.minutes-write-again"
          defaultMessage="Write again"
        />
      ) : status === "failed" ? (
        <FormattedMessage
          id="meetingCard.minutes-try-again"
          defaultMessage="The minutes could not be written, try again"
        />
      ) : (
        <FormattedMessage
          id="meetingCard.minutes-write"
          defaultMessage="Write the minutes"
        />
      )}
    </Button>
  );

  return (
    <>
      {status === "pending" ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} color="secondary" />
          <Typography className={styles.discreet}>
            <FormattedMessage
              id="meetingCard.minutes-pending"
              defaultMessage="Writing the minutes…"
            />
          </Typography>
        </Box>
      ) : (
        <>
          {status === "done" && transcript.summary && (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() => setOpen(true)}
            >
              {title}
            </Button>
          )}
          {isOwner && write}
          {outdated && (
            <Typography className={styles.discreet}>
              <FormattedMessage
                id="meetingCard.minutes-outdated"
                defaultMessage="The transcript changed since these minutes were written."
              />
            </Typography>
          )}
        </>
      )}
      <Error error={error} />
      {open && transcript.summary && (
        <Suspense fallback={null}>
          <MinutesModal
            open={open}
            title={title}
            content={transcript.summary}
            filename={`minutes-${slug}-${filenameDate(transcript.insertedAt)}`}
            onClose={() => setOpen(false)}
          />
        </Suspense>
      )}
    </>
  );
};

const Row = ({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    sx={{ p: "20px 24px", pl: 0, minWidth: "100%" }}
    display="flex"
    justifyContent="space-between"
  >
    <Typography sx={{ minWidth: "34%" }}>{label}</Typography>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        flexGrow: 1,
      }}
    >
      {children}
    </Box>
  </Box>
);

// The transcription of the meeting, saved by the server from the ohaio API when
// everybody left the room, and the chat messages, saved by the browser of the
// moderator (see OhaioButton.tsx).
export const MeetingReport: React.FC<{ slug: string }> = ({ slug }) => {
  const intl = useIntl();
  const { data, loading, error, startPolling, stopPolling } = useQuery<
    Data,
    Variables
  >(GET_ROOM_REPORT_QUERY, {
    variables: { slug },
    fetchPolicy: "cache-and-network",
  });

  // while minutes are being written
  const pending = !!data?.room?.transcripts.some(
    (transcript) => transcript.summaryStatus === "pending"
  );
  useEffect(() => {
    if (pending) startPolling(4000);
    else stopPolling();
    return () => stopPolling();
  }, [pending, startPolling, stopPolling]);

  if ((loading && !data) || error || !data?.room) return null;

  const { transcripts, chatLog } = data.room;
  const isOwner = data.room.authenticatedUserIsModerator;

  return (
    <>
      <Row
        label={
          <FormattedMessage
            id="meetingCard.report"
            defaultMessage="Meeting transcript"
          />
        }
      >
        {transcripts.length === 0 && (
          <Typography>
            <FormattedMessage
              id="meetingCard.no-report"
              defaultMessage="No transcript was saved for this meeting."
            />
          </Typography>
        )}
        {transcripts.map((transcript) => (
          <Box
            key={transcript.id}
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography>
              <FormattedDate value={new Date(transcript.insertedAt)} />{" "}
              <FormattedTime value={new Date(transcript.insertedAt)} />
            </Typography>
            {transcript.language && (
              <Typography className={styles.discreet}>
                {languageName(intl, transcript.language)}
              </Typography>
            )}
            <ShowButton
              title={
                <FormattedMessage
                  id="meetingCard.report"
                  defaultMessage="Meeting transcript"
                />
              }
              filename={`transcription-${slug}-${filenameDate(
                transcript.insertedAt
              )}.txt`}
              content={transcript.content}
            />
            <Minutes transcript={transcript} isOwner={isOwner} slug={slug} />
            {isOwner && (
              <DeleteButton
                mutation={DELETE_TRANSCRIPT_MUTATION}
                id={transcript.id}
                confirmation={
                  <FormattedMessage
                    id="meetingCard.delete-report-confirmation"
                    defaultMessage="Delete this meeting transcript?"
                  />
                }
              />
            )}
          </Box>
        ))}
      </Row>
      {chatLog && (
        <>
          <Divider />
          <Row
            label={
              <FormattedMessage
                id="meetingCard.chat-messages"
                defaultMessage="Chat messages"
              />
            }
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography>
                <FormattedDate value={new Date(chatLog.insertedAt)} />{" "}
                <FormattedTime value={new Date(chatLog.insertedAt)} />
              </Typography>
              <ShowButton
                title={
                  <FormattedMessage
                    id="meetingCard.chat-messages"
                    defaultMessage="Chat messages"
                  />
                }
                filename={`chat-${slug}.txt`}
                content={chatLog.content}
              />
              {isOwner && (
                <DeleteButton
                  mutation={DELETE_CHAT_LOG_MUTATION}
                  id={chatLog.id}
                  confirmation={
                    <FormattedMessage
                      id="meetingCard.delete-chat-confirmation"
                      defaultMessage="Delete the chat messages of this meeting?"
                    />
                  }
                />
              )}
            </Box>
          </Row>
        </>
      )}
    </>
  );
};
